import { useSyncExternalStore } from "react";
import type { CmmdDeviceRegistryDevice, CmmdDeviceRegistrySnapshot } from "@cmmd-forge/contracts";

import { cmmdApiClient } from "../environments/cmmdApi";

type Listener = () => void;

export interface CmmdDeviceRegistryResourceSnapshot {
  readonly registry: CmmdDeviceRegistrySnapshot | null;
  readonly isLoading: boolean;
  readonly error: string | null;
}

const INITIAL_SNAPSHOT: CmmdDeviceRegistryResourceSnapshot = {
  registry: null,
  isLoading: false,
  error: null,
};

export function applyCmmdDeviceRevocation(
  registry: CmmdDeviceRegistrySnapshot,
  revokedDevice: CmmdDeviceRegistryDevice,
): CmmdDeviceRegistrySnapshot {
  return {
    ...registry,
    devices: registry.devices.map((device) =>
      device.id === revokedDevice.id ? revokedDevice : device,
    ),
    runtimes: registry.runtimes.map((runtime) =>
      runtime.deviceId === revokedDevice.id ? { ...runtime, status: "revoked" } : runtime,
    ),
  };
}

function createCmmdDeviceRegistryResource() {
  let snapshot = INITIAL_SNAPSHOT;
  const listeners = new Set<Listener>();

  function emit(next: CmmdDeviceRegistryResourceSnapshot): void {
    snapshot = next;
    for (const listener of listeners) listener();
  }

  return {
    subscribe(listener: Listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    read(): CmmdDeviceRegistryResourceSnapshot {
      return snapshot;
    },
    publish(registry: CmmdDeviceRegistrySnapshot): void {
      emit({ registry, isLoading: false, error: null });
    },
    publishDeviceRevocation(revokedDevice: CmmdDeviceRegistryDevice): void {
      if (snapshot.registry === null) return;
      emit({
        registry: applyCmmdDeviceRevocation(snapshot.registry, revokedDevice),
        isLoading: false,
        error: null,
      });
    },
    async refresh(): Promise<CmmdDeviceRegistrySnapshot> {
      emit({ ...snapshot, isLoading: true, error: null });
      try {
        const registry = await cmmdApiClient.getDeviceRegistry();
        emit({ registry, isLoading: false, error: null });
        return registry;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Could not load CMMD device registry.";
        emit({ ...snapshot, isLoading: false, error: message });
        throw error;
      }
    },
  };
}

export const cmmdDeviceRegistryResource = createCmmdDeviceRegistryResource();

export function useCmmdDeviceRegistrySnapshot(): CmmdDeviceRegistryResourceSnapshot {
  return useSyncExternalStore(
    cmmdDeviceRegistryResource.subscribe,
    cmmdDeviceRegistryResource.read,
    cmmdDeviceRegistryResource.read,
  );
}
